import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Package, Plus, Search, Trash2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { DataTablePagination } from '@/components/data-table/data-table-pagination';
import { useDebounce } from '@/hooks/use-debounce';
import { useBarcodeScan } from '@/hooks/use-barcode-scan';
import { useDepartments } from '@/hooks/use-departments';
import { useProducts } from '@/hooks/use-products';
import { useCanSeeCost } from '@/hooks/use-pos-config';
import { cn, formatCurrency, formatNumber } from '@/lib/utils';
import { placeholderProductRow, toPromotionProductRow, type PromotionProductRow } from './promotion-product-directory';

const PER_PAGE_OPTIONS = [10, 25, 50, 100];

// ── shared row bits ────────────────────────────────────────────────────────

const AVATAR_TONES = [
  'bg-emerald-100 text-emerald-700',
  'bg-sky-100 text-sky-700',
  'bg-violet-100 text-violet-700',
  'bg-amber-100 text-amber-700',
  'bg-rose-100 text-rose-700',
  'bg-teal-100 text-teal-700',
];

function toneFor(seed: string): string {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return AVATAR_TONES[h % AVATAR_TONES.length];
}

/**
 * The product's own photo when it has one, else the tenant's matched generic
 * image (the products service fills `commonImageUrl` for products lacking their
 * own), else a tinted initial tile. Framed like the product list's thumbnail so
 * a photo on a white background doesn't bleed into the row.
 */
function ProductAvatar({ row }: { row: PromotionProductRow }) {
  const [errored, setErrored] = useState(false);
  const showImage = !!row.imageUrl && !errored;
  return (
    <span
      className={cn(
        'flex h-[30px] w-[30px] shrink-0 items-center justify-center overflow-hidden rounded border border-border',
        showImage ? 'bg-muted' : toneFor(row.id),
      )}
    >
      {showImage ? (
        <img
          src={row.imageUrl as string}
          alt=""
          loading="lazy"
          onError={() => setErrored(true)}
          className="h-full w-full object-cover"
        />
      ) : (
        <span aria-hidden="true" className="text-xs font-semibold uppercase">
          {(row.name?.trim() || '?').charAt(0)}
        </span>
      )}
    </span>
  );
}

function displayName(row: PromotionProductRow): string {
  return row.name?.trim() || 'Unavailable product';
}

function matchesQuery(row: PromotionProductRow, q: string): boolean {
  if (!q) return true;
  const needle = q.toLowerCase();
  return [row.name, row.sku, row.primaryBarcode].some((v) => !!v && v.toLowerCase().includes(needle));
}

// ── bulk picker modal ──────────────────────────────────────────────────────

interface ProductSelectModalProps {
  onClose: () => void;
  title: string;
  /** Ids already on the list — hidden from the results, per the design. */
  excludeIds: string[];
  locationId?: string;
  onAdd: (rows: PromotionProductRow[]) => void;
}

/**
 * Full-catalogue product browser stacked on top of the promotion dialog. Only
 * mounted while it's open, so its filters and pending selection reset naturally
 * between visits.
 *
 * Portalled to <body>: the parent Modal keeps a lingering `transform` from its
 * zoom-in animation, which would otherwise become the containing block for a
 * `position: fixed` child and trap this inside the dialog.
 */
function ProductSelectModal({ onClose, title, excludeIds, locationId, onAdd }: ProductSelectModalProps) {
  const [search, setSearch] = useState('');
  const scan = useBarcodeScan((code) => setSearch(code));
  const [departmentId, setDepartmentId] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(100);
  const [picked, setPicked] = useState<Map<string, PromotionProductRow>>(new Map());
  const debouncedSearch = useDebounce(search, 300);
  const canSeeCost = useCanSeeCost();

  const { data: deptData } = useDepartments({ locationId: locationId || undefined });
  const departmentOptions = useMemo(
    () => ((deptData?.data as any[]) ?? []).map((d) => ({ value: d.id, label: d.name })),
    [deptData],
  );

  // Reset to page 1 whenever the filters change, so a narrow result set doesn't
  // land the user on an empty trailing page.
  const filterKey = `${debouncedSearch}|${departmentId}|${perPage}`;
  const [prevFilterKey, setPrevFilterKey] = useState(filterKey);
  if (filterKey !== prevFilterKey) {
    setPrevFilterKey(filterKey);
    setPage(1);
  }

  const { data, isFetching } = useProducts({
    page,
    perPage,
    search: debouncedSearch || undefined,
    departmentId: departmentId || undefined,
    locationId: locationId || undefined,
    isActive: true,
  });

  const excluded = useMemo(() => new Set(excludeIds), [excludeIds]);
  // Already-added products are dropped client-side (the list endpoint has no
  // "exclude these ids" filter), so a page can render fewer rows than the
  // pagination strip's per-page count. The banner above the table explains it.
  const results = useMemo(
    () => (((data as any)?.data as any[]) ?? []).filter((p) => !excluded.has(p.id)).map(toPromotionProductRow),
    [data, excluded],
  );
  const pagination = (data as any)?.pagination;
  const total = pagination?.total ?? results.length;
  const totalPages = pagination?.totalPages ?? 1;

  const toggle = (row: PromotionProductRow) => {
    setPicked((prev) => {
      const next = new Map(prev);
      if (next.has(row.id)) next.delete(row.id);
      else next.set(row.id, row);
      return next;
    });
  };

  const allShownPicked = results.length > 0 && results.every((r) => picked.has(r.id));
  const toggleAllShown = () => {
    setPicked((prev) => {
      const next = new Map(prev);
      if (allShownPicked) results.forEach((r) => next.delete(r.id));
      else results.forEach((r) => next.set(r.id, r));
      return next;
    });
  };

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const columnCount = canSeeCost ? 6 : 5;

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-6" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} aria-hidden="true" />
      <div className="relative z-10 flex max-h-[calc(100vh-2rem)] w-full max-w-5xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
        <div className="flex shrink-0 items-center justify-between border-b border-border px-5 py-3">
          <h2 className="text-base font-semibold text-foreground">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="shrink-0 space-y-3 px-5 pb-3 pt-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative min-w-0 flex-1 sm:max-w-sm">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={scan.onKeyDown}
                onBlur={scan.onBlur}
                placeholder="Search"
                aria-label="Search products"
                className="h-9 w-full rounded-lg border border-input bg-white pl-9 pr-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
              />
            </div>
            <SearchableSelect
              className="w-56"
              options={departmentOptions}
              value={departmentId}
              onChange={setDepartmentId}
              placeholder="Departments"
              searchPlaceholder="Search departments..."
              emptyMessage="No departments"
            />
          </div>
          {excludeIds.length > 0 && (
            <p className="text-sm text-primary">
              Products that have already been added to the promotion will not be displayed in this list.
            </p>
          )}
        </div>

        <div className="flex-1 overflow-auto px-5">
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className="w-10 px-3 py-2.5">
                    <input
                      type="checkbox"
                      checked={allShownPicked}
                      onChange={toggleAllShown}
                      aria-label="Select all shown products"
                      className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                    />
                  </th>
                  <th className="px-3 py-2.5 text-left text-xs font-medium text-muted-foreground">Name</th>
                  <th className="px-3 py-2.5 text-right text-xs font-medium text-muted-foreground">Sale Price</th>
                  {canSeeCost && (
                    <th className="px-3 py-2.5 text-right text-xs font-medium text-muted-foreground">Cost</th>
                  )}
                  <th className="px-3 py-2.5 text-left text-xs font-medium text-muted-foreground">UoM</th>
                  <th className="px-3 py-2.5 text-left text-xs font-medium text-muted-foreground">Department</th>
                </tr>
              </thead>
              <tbody>
                {isFetching && results.length === 0 ? (
                  <tr>
                    <td colSpan={columnCount} className="px-3 py-12 text-center text-sm text-muted-foreground">
                      Loading products…
                    </td>
                  </tr>
                ) : results.length === 0 ? (
                  <tr>
                    <td colSpan={columnCount} className="px-3 py-12 text-center">
                      <Package className="mx-auto mb-2 h-8 w-8 text-muted-foreground/50" />
                      <p className="text-sm text-muted-foreground">No products found</p>
                    </td>
                  </tr>
                ) : (
                  results.map((row) => {
                    const isPicked = picked.has(row.id);
                    return (
                      <tr
                        key={row.id}
                        onClick={() => toggle(row)}
                        className={cn(
                          'cursor-pointer border-b border-border transition-colors last:border-0',
                          isPicked ? 'bg-primary/5' : 'hover:bg-muted/30',
                        )}
                      >
                        <td className="px-3 py-2.5">
                          <input
                            type="checkbox"
                            checked={isPicked}
                            onChange={() => toggle(row)}
                            onClick={(e) => e.stopPropagation()}
                            aria-label={`Select ${displayName(row)}`}
                            className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                          />
                        </td>
                        <td className="px-3 py-2.5">
                          <div className="flex items-center gap-2.5">
                            <ProductAvatar row={row} />
                            <div className="min-w-0">
                              <div className="truncate text-sm font-medium text-foreground">{displayName(row)}</div>
                              <div className="truncate text-xs text-muted-foreground">
                                {row.primaryBarcode || row.sku || '—'}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-2.5 text-right text-sm font-medium text-primary">
                          {row.sellingPrice === null ? '—' : formatCurrency(row.sellingPrice)}
                        </td>
                        {canSeeCost && (
                          <td className="px-3 py-2.5 text-right">
                            <div className="text-sm">{row.cost === null ? '—' : formatCurrency(row.cost)}</div>
                            <div
                              className={cn(
                                'text-xs',
                                (row.inStockQty ?? 0) > 0 ? 'text-muted-foreground' : 'text-red-500',
                              )}
                            >
                              {(row.inStockQty ?? 0) > 0 ? `${formatNumber(row.inStockQty)} In Stock` : 'Out of Stock'}
                            </div>
                          </td>
                        )}
                        <td className="px-3 py-2.5 text-sm text-muted-foreground">{row.unit || 'each'}</td>
                        <td className="px-3 py-2.5 text-sm text-muted-foreground">{row.departmentName || '—'}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="shrink-0 border-t border-border">
          <DataTablePagination
            page={page}
            totalPages={totalPages}
            total={total}
            perPage={perPage}
            onPageChange={setPage}
            perPageOptions={PER_PAGE_OPTIONS}
            onPerPageChange={setPerPage}
          />
        </div>

        <div className="flex shrink-0 items-center justify-end gap-3 border-t border-border bg-muted/30 px-5 py-3">
          <span className="mr-auto text-sm text-muted-foreground">
            {picked.size} product{picked.size === 1 ? '' : 's'} selected
          </span>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              onAdd([...picked.values()]);
              onClose();
            }}
            disabled={picked.size === 0}
          >
            Add Products
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ── single-product type-ahead ──────────────────────────────────────────────

function ProductTypeahead({
  excludeIds,
  locationId,
  onPick,
  placeholder,
}: {
  excludeIds: string[];
  locationId?: string;
  onPick: (row: PromotionProductRow) => void;
  placeholder: string;
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounced = useDebounce(query, 300);
  const scan = useBarcodeScan((code) => {
    setQuery(code);
    setOpen(true);
  });

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const { data, isFetching } = useProducts(
    { perPage: 25, search: debounced || undefined, locationId: locationId || undefined, isActive: true },
    { enabled: open },
  );

  const excluded = useMemo(() => new Set(excludeIds), [excludeIds]);
  const results = useMemo(
    () => (((data as any)?.data as any[]) ?? []).filter((p) => !excluded.has(p.id)).map(toPromotionProductRow),
    [data, excluded],
  );

  return (
    <div ref={containerRef} className="relative w-full sm:max-w-xs">
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <input
        type="text"
        value={query}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onKeyDown={scan.onKeyDown}
        onBlur={scan.onBlur}
        placeholder={placeholder}
        className="h-9 w-full rounded-lg border border-input bg-white pl-9 pr-9 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
      />
      <ChevronDown
        aria-hidden="true"
        className={cn(
          'pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground transition-transform',
          open && 'rotate-180',
        )}
      />
      {open && (
        <div className="absolute z-50 mt-1 w-full overflow-hidden rounded-lg border border-border bg-white shadow-lg">
          <div className="max-h-64 overflow-y-auto py-1">
            {isFetching && results.length === 0 ? (
              <div className="px-3 py-4 text-center text-sm text-muted-foreground">Searching…</div>
            ) : results.length === 0 ? (
              <div className="px-3 py-4 text-center text-sm text-muted-foreground">No matching products</div>
            ) : (
              results.map((row) => (
                <button
                  key={row.id}
                  type="button"
                  onClick={() => {
                    onPick(row);
                    setQuery('');
                    setOpen(false);
                  }}
                  className="flex w-full items-center gap-2.5 px-3 py-1.5 text-left transition-colors hover:bg-muted"
                >
                  <ProductAvatar row={row} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm text-foreground">{displayName(row)}</span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {row.primaryBarcode || row.sku || '—'}
                    </span>
                  </span>
                  <Plus className="h-4 w-4 shrink-0 text-primary" />
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── the selected-products section ──────────────────────────────────────────

interface PromotionProductTableProps {
  /** Section heading, e.g. "Applicable Products". */
  title: string;
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  /** Resolved detail for every selected id, from useProductDirectory. */
  rows: Record<string, PromotionProductRow>;
  /** Feeds newly picked rows back into the directory cache. */
  onRemember: (rows: any[]) => void;
  locationId?: string;
  pickerTitle?: string;
  addLabel?: string;
  searchPlaceholder?: string;
  emptyMessage: string;
  hint?: ReactNode;
  /** Optional trailing column — used for the per-product promo price override. */
  extraColumn?: { header: string; width?: string; render: (row: PromotionProductRow) => ReactNode };
  /** Notify the parent so it can hand the keyboard to the stacked picker. */
  onPickerOpenChange?: (open: boolean) => void;
}

export function PromotionProductTable({
  title,
  selectedIds,
  onChange,
  rows,
  onRemember,
  locationId,
  pickerTitle = 'Select Products',
  addLabel = 'Add Multiple Products',
  searchPlaceholder = 'Search for a product to add',
  emptyMessage,
  hint,
  extraColumn,
  onPickerOpenChange,
}: PromotionProductTableProps) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);

  const setPicker = (next: boolean) => {
    setPickerOpen(next);
    onPickerOpenChange?.(next);
  };

  const add = (incoming: PromotionProductRow[]) => {
    onRemember(incoming);
    const existing = new Set(selectedIds);
    const additions = incoming.map((r) => r.id).filter((id) => !existing.has(id));
    if (additions.length) onChange([...selectedIds, ...additions]);
  };

  const remove = (id: string) => onChange(selectedIds.filter((x) => x !== id));

  const visible = useMemo(
    () =>
      selectedIds.map((id) => rows[id] ?? placeholderProductRow(id)).filter((row) => matchesQuery(row, filter.trim())),
    [selectedIds, rows, filter],
  );

  const totalPages = Math.max(1, Math.ceil(visible.length / perPage));
  // Clamp during render rather than in an effect — removing the last row on the
  // final page should not flash an empty table before correcting itself.
  const currentPage = Math.min(page, totalPages);
  const pageRows = useMemo(
    () => visible.slice((currentPage - 1) * perPage, currentPage * perPage),
    [visible, currentPage, perPage],
  );

  const columnCount = 4 + (extraColumn ? 1 : 0) + 1;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h4 className="text-sm font-semibold text-foreground">{title}</h4>
        <Button type="button" variant="primary-outline" size="sm" onClick={() => setPicker(true)}>
          {addLabel}
        </Button>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <ProductTypeahead
          excludeIds={selectedIds}
          locationId={locationId}
          onPick={(row) => add([row])}
          placeholder={searchPlaceholder}
        />
        {selectedIds.length > 0 && (
          <div className="relative w-full sm:max-w-xs">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              value={filter}
              onChange={(e) => {
                setFilter(e.target.value);
                setPage(1);
              }}
              placeholder="Search"
              aria-label={`Search ${title}`}
              className="h-9 w-full rounded-lg border border-input bg-white pl-9 pr-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
            />
          </div>
        )}
      </div>

      <div className="overflow-hidden rounded-lg border border-border">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="px-3 py-2.5 text-left text-xs font-medium text-muted-foreground">Product</th>
                <th className="px-3 py-2.5 text-left text-xs font-medium text-muted-foreground">UOM</th>
                <th className="px-3 py-2.5 text-left text-xs font-medium text-muted-foreground">Primary Barcode</th>
                <th className="px-3 py-2.5 text-right text-xs font-medium text-muted-foreground">Sale Price ($)</th>
                {extraColumn && (
                  <th
                    className={cn(
                      'px-3 py-2.5 text-right text-xs font-medium text-muted-foreground',
                      extraColumn.width,
                    )}
                  >
                    {extraColumn.header}
                  </th>
                )}
                <th className="w-14 px-3 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {pageRows.length === 0 ? (
                <tr>
                  <td colSpan={columnCount} className="px-3 py-10 text-center">
                    <Package className="mx-auto mb-2 h-7 w-7 text-muted-foreground/50" />
                    <p className="text-sm text-muted-foreground">
                      {selectedIds.length === 0 ? emptyMessage : 'No products match that search'}
                    </p>
                  </td>
                </tr>
              ) : (
                pageRows.map((row) => (
                  <tr key={row.id} className="border-b border-border last:border-0">
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2.5">
                        <ProductAvatar row={row} />
                        <span className="min-w-0 truncate text-sm font-medium text-foreground">{displayName(row)}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-sm text-muted-foreground">{row.unit || 'each'}</td>
                    <td className="px-3 py-2.5 text-sm text-muted-foreground">
                      {row.primaryBarcode || row.sku || '—'}
                    </td>
                    <td className="px-3 py-2.5 text-right text-sm text-foreground">
                      {row.sellingPrice === null ? '—' : formatCurrency(row.sellingPrice)}
                    </td>
                    {extraColumn && <td className="px-3 py-2.5 text-right">{extraColumn.render(row)}</td>}
                    <td className="px-3 py-2.5 text-right">
                      <button
                        type="button"
                        onClick={() => remove(row.id)}
                        aria-label={`Remove ${displayName(row)}`}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-red-200 text-red-500 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {visible.length > 0 && (
          <div className="border-t border-border">
            <DataTablePagination
              page={currentPage}
              totalPages={totalPages}
              total={visible.length}
              perPage={perPage}
              onPageChange={setPage}
              perPageOptions={PER_PAGE_OPTIONS}
              onPerPageChange={(n) => {
                setPerPage(n);
                setPage(1);
              }}
            />
          </div>
        )}
      </div>

      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}

      {pickerOpen && (
        <ProductSelectModal
          onClose={() => setPicker(false)}
          title={pickerTitle}
          excludeIds={selectedIds}
          locationId={locationId}
          onAdd={add}
        />
      )}
    </div>
  );
}
